import { CourseChanges } from '@/client/courses/types/course-changes.type';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from './course-changes.styles';
import { PendingQuestionEdit, PendingSlideEdit } from './course-changes.types';
import {
  countQuestionChanges,
  countSlideChanges,
  getContentTypeName,
} from './course-changes.utils';
import { QuestionChangesDisplay } from './QuestionChangesDisplay';
import { SlideChangesDisplay } from './SlideChangesDisplay';

export type CourseChangesReviewProps = {
  courseChanges: CourseChanges;
  onSlideEdit: (edit: PendingSlideEdit) => void;
  onQuestionEdit: (edit: PendingQuestionEdit) => void;
  pendingSlideEdits: PendingSlideEdit[];
  pendingQuestionEdits: PendingQuestionEdit[];
  renderContainer?: (children: React.ReactNode) => React.ReactNode;
};

export const CourseChangesReview: React.FC<CourseChangesReviewProps> = ({
  courseChanges,
  onSlideEdit,
  onQuestionEdit,
  pendingSlideEdits,
  pendingQuestionEdits,
  renderContainer,
}) => {
  const { t } = useTranslation();

  // Create a map of change to its original index for efficient lookup
  const changeToIndexMap = new Map(
    courseChanges.contentChanges.map((change, index) => [change, index]),
  );

  const contentWithSlides = courseChanges.contentChanges
    .filter((change) => change.slideChanges && change.slideChanges.length > 0)
    .map((change) => ({
      change,
      contentIndex: changeToIndexMap.get(change) ?? -1,
    }))
    .filter((item) => item.contentIndex !== -1);

  const contentWithQuestions = courseChanges.contentChanges
    .filter(
      (change) => change.questionChanges && change.questionChanges.length > 0,
    )
    .map((change) => ({
      change,
      contentIndex: changeToIndexMap.get(change) ?? -1,
    }))
    .filter((item) => item.contentIndex !== -1);

  // Handle empty state
  if (contentWithSlides.length === 0 && contentWithQuestions.length === 0) {
    return (
      <EmptyState>
        <i
          className="pi pi-info-circle"
          style={{ fontSize: '32px', marginBottom: '16px' }}
        />
        <p>{t('course.changes.noChanges')}</p>
      </EmptyState>
    );
  }

  return (
    <>
      {/* Render all slides first */}
      {contentWithSlides.map(({ change, contentIndex }, index) => {
        const slideChanges = change.slideChanges;

        if (!slideChanges) {
          return null;
        }

        const changeContent = (
          <SlideChangesDisplay
            className={`content-change-${contentIndex}`}
            slideChanges={slideChanges}
            contentTitle={change.title || getContentTypeName(change.type, t)}
            modificationCount={
              slideChanges.filter((slide) => countSlideChanges(slide) > 0)
                .length
            }
            isFirst={index === 0}
            contentIndex={contentIndex}
            onSlideEdit={onSlideEdit}
            pendingEdits={pendingSlideEdits}
          />
        );

        return (
          <div key={`slide-${contentIndex}-${index}`}>
            {renderContainer ? renderContainer(changeContent) : changeContent}
          </div>
        );
      })}

      {/* Render all quizzes second */}
      {contentWithQuestions.map(({ change, contentIndex }, index) => {
        if (!change.questionChanges) {
          return null;
        }

        const questionChanges = change.questionChanges;

        const changeContent = (
          <QuestionChangesDisplay
            className={`content-change-${index}`}
            questionChanges={questionChanges}
            contentTitle={change.title || getContentTypeName(change.type, t)}
            modificationCount={
              questionChanges.filter(
                (question) => countQuestionChanges(question) > 0,
              ).length
            }
            contentIndex={contentIndex}
            onQuestionEdit={onQuestionEdit}
            pendingEdits={pendingQuestionEdits}
          />
        );

        return (
          <div key={`question-${contentIndex}-${index}`}>
            {renderContainer ? renderContainer(changeContent) : changeContent}
          </div>
        );
      })}
    </>
  );
};
